import type { CSSProperties } from 'react';
import { ThunderboltFilled } from '@ant-design/icons';
import { Flex, Tooltip, Typography, theme } from 'antd';
import { coresDaOferta, veuDaCor } from '@titularis/ui';
import { contar } from '../texto';
import { useAbrirOferta } from './Oferta';
import './chamada-do-plano.css';

/**
 * A chamada para assinar, logo acima do perfil na barra lateral.
 *
 * É o lugar que a Voa usa para a mesma coisa, e a razão de ele funcionar é a
 * permanência: a faixa do topo se dispensa num clique e some na tela de Plano,
 * o paywall só aparece depois que a cota acaba, e entre um e outro **não havia
 * nada** dizendo que existe um jeito de continuar estudando. Aqui há, em toda
 * tela, sem interromper ninguém.
 *
 * O desenho é o da Voa, medido nela em 06/09/2026: 48px de altura, ícone à
 * esquerda, título em cima e a cota da semana embaixo em texto menor, tudo sobre
 * um degradê que anda devagar. **As cores são nossas** - o verde de Cartórios,
 * o azul da 1ª fase e a ameixa da Legislação (`coresDaOferta`), que é
 * literalmente o que o botão promete desbloquear, e não o azul-magenta de lá.
 *
 * Recolhida, vira o quadrado do ícone: o rótulo não caberia em 72px, e o
 * `Tooltip` diz o que o quadrado faz.
 *
 * **O rótulo dizia "Desbloquear tudo"** até 15/10/2026, quando a 1ª fase virou
 * produto à parte do Enac: nenhuma compra abre tudo, e a promessa passou a ser a
 * mesma do convite da oferta - estudar sem a cota da semana.
 */
export function ChamadaDoPlano({
  recolhida,
  restante,
  antesDeAbrir,
}: {
  recolhida: boolean;
  /** Quanto ainda resta na semana - é o que dá urgência ao convite. */
  restante: { answers: number; commentaries: number };
  /**
   * Roda antes de a oferta abrir.
   *
   * Existe para a gaveta do celular: ela e o modal ocupam a mesma camada, e a
   * oferta nasceria **atrás** do menu aberto - o clique parecia não ter feito
   * nada. Quem abre a gaveta é o layout, então é ele quem a fecha.
   */
  antesDeAbrir?: () => void;
}) {
  const { token } = theme.useToken();
  const abrirOferta = useAbrirOferta();

  const [verde, azul, ameixa] = coresDaOferta;

  /*
   * As cores vão para o CSS por variável, e não por regra: o degradê e o brilho
   * moram na folha de estilo (é lá que dá para animar), e o tema é quem sabe as
   * cores. Mesma divisão do `noticias-recentes.css`.
   */
  const cores = {
    '--chamada-1': verde,
    '--chamada-2': azul,
    '--chamada-3': ameixa,
    '--chamada-brilho-1': veuDaCor(verde, 0.5),
    '--chamada-brilho-3': veuDaCor(ameixa, 0.38),
  } as CSSProperties;

  const acabou = restante.answers === 0;
  /*
   * **"na semana", e não "nesta semana", porque a linha é estreita.**
   *
   * Medido no navegador em 12/09/2026, com a barra aberta: sobram 163 pixels
   * para a frase, e "Restam 4 questões nesta semana." ocupa 169 - o texto saía
   * cortado em reticências no dia em que a cota virou semanal. A forma curta
   * mede 154, e ainda cabe com a cota em dois dígitos (161), que é o que o
   * backoffice pode pôr lá a qualquer momento.
   */
  const frase = acabou
    ? 'Sua cota da semana acabou.'
    : `Restam ${contar(restante.answers, 'questão', 'questões')} na semana.`;

  if (recolhida) {
    return (
      <Tooltip title="Estudar sem limite" placement="right">
        <button
          type="button"
          className="titularis-chamada-do-plano"
          aria-label="Estudar sem limite"
          onClick={() => {
            antesDeAbrir?.();
            abrirOferta();
          }}
          style={{
            ...cores,
            justifyContent: 'center',
            width: 40,
            height: 40,
            padding: 0,
            borderRadius: token.borderRadius,
            // Centrado nos 72px da barra recolhida, com o mesmo respiro do perfil.
            margin: '0 auto 12px',
          }}
        >
          <ThunderboltFilled style={{ fontSize: token.fontSizeLG }} />
        </button>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      className="titularis-chamada-do-plano"
      onClick={() => {
        antesDeAbrir?.();
        abrirOferta();
      }}
      style={{
        ...cores,
        gap: 10,
        minHeight: 48,
        padding: '6px 12px',
        borderRadius: token.borderRadius,
        /*
         * Oito pixels de cada lado, e não os dezesseis do perfil: na Voa a
         * chamada é mais larga que o resto da barra de propósito - ela avança
         * sobre a margem para pesar mais que os itens de menu ao redor.
         */
        margin: '0 8px 12px',
        width: 'auto',
      }}
    >
      <ThunderboltFilled style={{ fontSize: token.fontSizeLG, flexShrink: 0 }} />
      <Flex vertical style={{ minWidth: 0 }}>
        {/*
          `color: inherit` nos dois: o `Typography.Text` nasce com a cor do
          texto do tema, que sobre o degradê seria um cinza escuro ilegível.
        */}
        <Typography.Text strong ellipsis style={{ color: 'inherit', lineHeight: 1.3 }}>
          Estudar sem limite
        </Typography.Text>
        <Typography.Text
          ellipsis
          style={{ color: 'inherit', opacity: 0.75, fontSize: 11, lineHeight: 1.3 }}
        >
          {frase}
        </Typography.Text>
      </Flex>
    </button>
  );
}
